import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBrand } from '../lib/api/brands';
import {
  BrandCommitment,
  useCommitments,
  useCreateCommitment,
  useDeleteCommitment,
  usePatchCommitment,
} from '../lib/api/commitments';
import {
  CUSTOM_SLOT_KEY,
  SLOT_CATALOG,
  SlotDef,
  getSlotDef,
  slotLabel,
} from '../lib/visibilitySlots';
import { BrandAvatar } from '../lib/brandAvatars';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { useToast } from '../components/ui/Toast';

const todayYear = () => new Date().getFullYear();

const status = (c: BrandCommitment): { label: string; color: string } => {
  if (c.mode === 'always_on') {
    return c.delivered_state
      ? { label: 'Active', color: 'var(--seasonal)' }
      : { label: 'Not yet', color: 'var(--ink-4)' };
  }
  if (c.target === 0) return { label: '—', color: 'var(--ink-4)' };
  const ratio = c.delivered / c.target;
  if (ratio >= 1) return { label: 'Done', color: 'var(--seasonal)' };
  if (ratio >= 0.5) return { label: 'On track', color: 'var(--sitewide)' };
  return { label: 'Behind', color: 'var(--warn)' };
};

const ProgressBar = ({ value, max }: { value: number; max: number }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div
      style={{
        width: 120,
        height: 6,
        background: 'var(--bg-subtle)',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: pct >= 100 ? 'var(--seasonal)' : 'var(--ink-3)',
          transition: 'width 0.2s',
        }}
      />
    </div>
  );
};

const CommitmentRow = ({
  commitment,
  brandId,
  year,
}: {
  commitment: BrandCommitment;
  brandId: string;
  year: number;
}) => {
  const patch = usePatchCommitment(brandId, year);
  const del = useDeleteCommitment(brandId, year);
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draftTarget, setDraftTarget] = useState(commitment.target);
  const [draftDelivered, setDraftDelivered] = useState(commitment.delivered_manual);
  const [draftNotes, setDraftNotes] = useState(commitment.notes);

  const s = status(commitment);
  const isAlwaysOn = commitment.mode === 'always_on';
  const isManualCounted =
    commitment.slot === 'website_collection_page' ||
    commitment.slot === 'website_brand_page' ||
    commitment.slot === CUSTOM_SLOT_KEY;

  const save = async () => {
    try {
      await patch.mutateAsync({
        id: commitment.id,
        patch: {
          target: draftTarget,
          delivered_manual: draftDelivered,
          notes: draftNotes,
        },
      });
      setEditing(false);
    } catch {
      toast.push('Could not save');
    }
  };

  const remove = async () => {
    try {
      await del.mutateAsync(commitment.id);
      toast.push('Slot removed from plan');
    } catch {
      toast.push('Could not remove slot');
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto auto auto',
        alignItems: 'center',
        gap: 14,
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-soft)',
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 550, color: 'var(--ink)' }}>
          {slotLabel(commitment.slot, commitment.custom_label)}
        </div>
        {commitment.notes && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
            {commitment.notes}
          </div>
        )}
      </div>

      {/* Target */}
      <div className="mono" style={{ fontSize: 12, color: 'var(--ink-4)', minWidth: 70, textAlign: 'right' }}>
        {isAlwaysOn ? 'year-round' : `target ${commitment.target}`}
      </div>

      {/* Delivered + bar / toggle */}
      {isAlwaysOn ? (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={commitment.delivered_state}
            onChange={(e) =>
              patch.mutate({
                id: commitment.id,
                patch: { delivered_state: e.target.checked },
              })
            }
          />
          {commitment.delivered_state ? 'In place' : 'Not yet'}
        </label>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ProgressBar value={commitment.delivered} max={commitment.target} />
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 36 }}>
            {commitment.delivered}/{commitment.target}
          </div>
        </div>
      )}

      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: s.color,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {s.label}
      </span>

      <div style={{ display: 'flex', gap: 4 }}>
        {isManualCounted && !isAlwaysOn && (
          <button
            type="button"
            className="icon-btn"
            title="Record one delivered"
            onClick={() =>
              patch.mutate({
                id: commitment.id,
                patch: { delivered_manual: commitment.delivered_manual + 1 },
              })
            }
          >
            <Icon name="plus" size={12} />
          </button>
        )}
        <button
          type="button"
          className="icon-btn"
          title="Edit"
          onClick={() => {
            setDraftTarget(commitment.target);
            setDraftDelivered(commitment.delivered_manual);
            setDraftNotes(commitment.notes);
            setEditing(true);
          }}
        >
          <Icon name="settings" size={12} />
        </button>
        <button
          type="button"
          className="icon-btn"
          title="Remove from plan"
          onClick={remove}
          style={{ color: 'var(--ink-4)' }}
        >
          <Icon name="x" size={12} />
        </button>
      </div>

      {editing && (
        <div
          style={{
            gridColumn: '1 / -1',
            background: 'var(--bg-subtle)',
            padding: 14,
            borderRadius: 'var(--r-md)',
            marginTop: 6,
            display: 'grid',
            gridTemplateColumns: '160px 160px 1fr auto',
            gap: 12,
            alignItems: 'end',
          }}
        >
          {!isAlwaysOn && (
            <label style={{ fontSize: 11, color: 'var(--ink-4)' }}>
              Target
              <input
                className="input"
                type="number"
                min={0}
                value={draftTarget}
                onChange={(e) => setDraftTarget(Number(e.target.value))}
                style={{ marginTop: 4 }}
              />
            </label>
          )}
          {isManualCounted && !isAlwaysOn && (
            <label style={{ fontSize: 11, color: 'var(--ink-4)' }}>
              Delivered (manual)
              <input
                className="input"
                type="number"
                min={0}
                value={draftDelivered}
                onChange={(e) => setDraftDelivered(Number(e.target.value))}
                style={{ marginTop: 4 }}
              />
            </label>
          )}
          <label style={{ fontSize: 11, color: 'var(--ink-4)' }}>
            Notes
            <input
              className="input"
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              placeholder="Anything to remember about this slot…"
              style={{ marginTop: 4 }}
            />
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={patch.isPending}>Save</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const AddSlotPicker = ({
  brandId,
  year,
  excludeKeys,
}: {
  brandId: string;
  year: number;
  excludeKeys: Set<string>;
}) => {
  const create = useCreateCommitment(brandId, year);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState<'counted' | 'always_on'>('counted');
  const [customLabel, setCustomLabel] = useState('');
  const [customTarget, setCustomTarget] = useState(1);

  const available = useMemo(
    () => SLOT_CATALOG.filter((s) => !excludeKeys.has(s.key)),
    [excludeKeys],
  );
  const grouped = useMemo(() => {
    const m = new Map<string, SlotDef[]>();
    for (const s of available) {
      const arr = m.get(s.group) ?? [];
      arr.push(s);
      m.set(s.group, arr);
    }
    return m;
  }, [available]);

  const addPredefined = async (slot: SlotDef) => {
    try {
      await create.mutateAsync({
        year,
        slot: slot.key,
        mode: slot.mode,
        target: slot.mode === 'always_on' ? 1 : 0,
      });
      setOpen(false);
    } catch {
      toast.push('Could not add slot');
    }
  };

  const addCustom = async () => {
    if (!customLabel.trim()) return;
    try {
      await create.mutateAsync({
        year,
        slot: CUSTOM_SLOT_KEY,
        custom_label: customLabel.trim(),
        mode: customMode,
        target: customMode === 'counted' ? customTarget : 1,
      });
      setOpen(false);
      setCustomLabel('');
      setCustomMode('counted');
      setCustomTarget(1);
    } catch {
      toast.push('Could not add custom slot');
    }
  };

  return (
    <div style={{ position: 'relative', marginTop: 16 }}>
      <Button variant="ghost" onClick={() => setOpen((v) => !v)}>
        <Icon name="plus" size={13} /> Add slot
      </Button>
      {open && (
        <div
          className="popover"
          style={{ left: 0, top: 36, padding: 10, minWidth: 320, maxHeight: 480, overflowY: 'auto' }}
        >
          {Array.from(grouped.entries()).map(([group, slots]) => (
            <div key={group} style={{ marginBottom: 8 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--ink-4)',
                  fontWeight: 600,
                  padding: '4px 8px',
                }}
              >
                {group}
              </div>
              {slots.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className="popover-item"
                  onClick={() => addPredefined(s)}
                >
                  <span style={{ fontSize: 12.5 }}>{s.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-4)' }}>
                    {s.mode === 'always_on' ? 'year-round' : 'counted'}
                  </span>
                </button>
              ))}
            </div>
          ))}
          <div
            style={{
              borderTop: '1px solid var(--border-soft)',
              marginTop: 6,
              padding: '10px 8px 4px',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--ink-4)',
                fontWeight: 600,
                marginBottom: 6,
              }}
            >
              Custom slot
            </div>
            <input
              className="input"
              placeholder="e.g. Catalogue feature, partner event…"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
              <button
                type="button"
                className={`filter-pill ${customMode === 'counted' ? 'active' : ''}`}
                onClick={() => setCustomMode('counted')}
              >
                Counted
              </button>
              <button
                type="button"
                className={`filter-pill ${customMode === 'always_on' ? 'active' : ''}`}
                onClick={() => setCustomMode('always_on')}
              >
                Year-round
              </button>
              {customMode === 'counted' && (
                <input
                  className="input"
                  type="number"
                  min={1}
                  value={customTarget}
                  onChange={(e) => setCustomTarget(Number(e.target.value))}
                  style={{ width: 80 }}
                />
              )}
            </div>
            <Button variant="primary" onClick={addCustom} disabled={!customLabel.trim() || create.isPending}>
              Add custom slot
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const BrandVisibility = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: brand } = useBrand(id);
  const [year, setYear] = useState(todayYear());
  const { data: commitments = [], isLoading } = useCommitments(id, year);
  const toast = useToast();

  if (!brand) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
        Loading…
      </div>
    );
  }

  if (!brand.has_visibility_plan) {
    return (
      <div>
        <div style={{ marginBottom: 12 }}>
          <button type="button" className="link" onClick={() => navigate(`/brands/${id}`)} style={{ fontSize: 12 }}>
            ← Back to {brand.name}
          </button>
        </div>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ color: 'var(--ink-3)', marginBottom: 12 }}>
            {brand.name} doesn't have a visibility plan yet.
          </p>
        </div>
      </div>
    );
  }

  const usedSlotKeys = new Set<string>(
    commitments.filter((c) => c.slot !== CUSTOM_SLOT_KEY).map((c) => c.slot),
  );

  // Render in the catalog's order (predefined slots first, custom at the end
  // sorted by created_at — backend default order). Without this, the row
  // order would jump every time a slot is added.
  const catalogIndex = new Map(SLOT_CATALOG.map((s, i) => [s.key, i]));
  const sortedCommitments = [...commitments].sort((a, b) => {
    const ai = catalogIndex.get(a.slot) ?? Infinity;
    const bi = catalogIndex.get(b.slot) ?? Infinity;
    if (ai !== bi) return ai - bi;
    return a.id - b.id;
  });

  const yearOptions = [todayYear() - 1, todayYear(), todayYear() + 1];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="link" onClick={() => navigate(`/brands/${id}`)} style={{ fontSize: 12 }}>
          ← Back to {brand.name}
        </button>
      </div>

      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <BrandAvatar brand={brand} size={48} />
          <div>
            <div className="page-eyebrow">Visibility plan</div>
            <h1 className="page-title">{brand.name}</h1>
            <p className="page-sub">
              Year-round commitments and how we're tracking against them.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="input"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: 110 }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
          Loading…
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {commitments.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
              No slots in this plan yet. Add one below.
            </div>
          ) : (
            sortedCommitments.map((c) => (
              <CommitmentRow key={c.id} commitment={c} brandId={brand.id} year={year} />
            ))
          )}
        </div>
      )}

      {id && (
        <AddSlotPicker
          brandId={id}
          year={year}
          excludeKeys={usedSlotKeys}
        />
      )}
    </div>
  );
};

export default BrandVisibility;
